import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { AlertTriangle, RefreshCw, MessageSquare } from 'lucide-react';
import { FeedbackDialog } from './FeedbackDialog';

// Functional wrapper to ensure hooks context is available
const ErrorFeedbackWrapper: React.FC<{
  errorMessage: string;
  errorStack: string;
  componentStack: string;
}> = ({ errorMessage, errorStack, componentStack }) => {
  return (
    <FeedbackDialog
      initialType="bug_report"
      initialMessage={`I encountered this error: ${errorMessage}`}
      errorDetails={`${errorStack}\n\nComponent Stack:${componentStack}`}
      trigger={
        <Button variant="ghost" size="sm" className="w-full">
          <MessageSquare className="w-4 h-4 mr-2" />
          Send Additional Details
        </Button>
      }
    />
  );
};

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
}

export class GlobalErrorBoundary extends Component<Props, State> {
  private static reportedErrors = new Set<string>();
  private static lastReportTime = 0;

  public state: State = {
    hasError: false
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Global error caught:', error, errorInfo);
    
    this.setState({
      hasError: true,
      error,
      errorInfo
    });

    // Auto-report critical errors (with deduplication & throttling)
    try {
      // Skip reports from crawlers/bots — stale chunk errors after deploys aren't actionable
      const ua = navigator.userAgent || '';
      if (/bot|crawl|spider|applebot|googlebot|bingbot|slurp|facebook|twitter|whatsapp|telegram|discord|linkedin|pinterest/i.test(ua)) {
        console.log('Skipping error report from bot:', ua);
        return;
      }

      const errorKey = `${error.message}::${window.location.pathname}`;
      const now = Date.now();
      const MIN_INTERVAL_MS = 60_000; // 1 minute between any reports

      if (
        GlobalErrorBoundary.reportedErrors.has(errorKey) ||
        now - GlobalErrorBoundary.lastReportTime < MIN_INTERVAL_MS
      ) {
        console.log('Skipping duplicate/throttled error report:', errorKey);
        return;
      }

      GlobalErrorBoundary.reportedErrors.add(errorKey);
      GlobalErrorBoundary.lastReportTime = now;

      const errorData = {
        type: 'error_report' as const,
        message: `Critical error: ${error.message}`,
        userAgent: navigator.userAgent,
        url: window.location.href,
        errorDetails: `${error.stack}\n\nComponent Stack:${errorInfo.componentStack}`,
      };

      // Use fetch instead of supabase client since we might be in a broken state
      fetch(`https://kbkjgzqrkkeiiyhqyqqg.supabase.co/functions/v1/send-feedback`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Imtia2pnenFya2tlaWl5aHF5cXFnIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTQ2NzQ5NDksImV4cCI6MjA3MDI1MDk0OX0.m2FZ4Yzc95HjMeeNPLnqNLpvlcjsTLvIJiVp5QD7L9M`,
        },
        body: JSON.stringify(errorData),
      }).catch(console.error);
    } catch (reportError) {
      console.error('Failed to auto-report error:', reportError);
    }
  }

  private handleReload = () => {
    window.location.reload();
  };

  private handleReset = () => {
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
  };

  public render() {
    if (this.state.hasError) {
      const errorMessage = this.state.error?.message || 'An unexpected error occurred';
      const errorStack = this.state.error?.stack || '';
      const componentStack = this.state.errorInfo?.componentStack || '';

      return (
        <div className="min-h-screen flex items-center justify-center p-4 bg-background">
          <Card className="w-full max-w-lg">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle className="w-5 h-5" />
                Something went wrong
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground">
                BRRGR encountered an unexpected error. We've automatically reported this issue.
              </p>
              
              <details className="text-sm">
                <summary className="cursor-pointer font-medium mb-2">Error details</summary>
                <div className="bg-muted p-3 rounded text-xs font-mono overflow-auto max-h-32">
                  <div className="mb-2">
                    <strong>Error:</strong> {errorMessage}
                  </div>
                  {errorStack && (
                    <div className="mb-2">
                      <strong>Stack:</strong>
                      <pre className="whitespace-pre-wrap mt-1">{errorStack}</pre>
                    </div>
                  )}
                  {componentStack && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre className="whitespace-pre-wrap mt-1">{componentStack}</pre>
                    </div>
                  )}
                </div>
              </details>

              <div className="flex flex-col sm:flex-row gap-2">
                <Button 
                  onClick={this.handleReload} 
                  className="flex-1"
                  variant="default"
                >
                  <RefreshCw className="w-4 h-4 mr-2" />
                  Reload Page
                </Button>
                <Button 
                  onClick={this.handleReset} 
                  variant="outline"
                  className="flex-1"
                >
                  Try Again
                </Button>
              </div>

              <div className="pt-2">
                <ErrorFeedbackWrapper
                  errorMessage={errorMessage}
                  errorStack={errorStack}
                  componentStack={componentStack}
                />
              </div>
            </CardContent>
          </Card>
        </div>
      );
    }

    return this.props.children;
  }
}